/**
 * Whether a newer build has shipped, and the user's "Reload" click.
 *
 * Module-level, in the shape of `shared/status/saveStatusStore`: `subscribe` +
 * `getSnapshot` feed `useSyncExternalStore`, so the rail and the phone's top
 * row read one value while the checker (not a component) writes it.
 */
import { hasUnsavedWork, subscribeToUnsavedWork } from '@shared/status/unsavedWork';

import { reloadPage } from './reloadPage';

/** What the update chip renders from. */
export interface AppUpdateSnapshot {
  /** The deployed version differs from this bundle's. */
  updateAvailable: boolean;
  /** Reload was clicked while edits were unsaved; it fires once they land. */
  reloadPending: boolean;
}

let snapshot: AppUpdateSnapshot = { updateAvailable: false, reloadPending: false };
const listeners = new Set<() => void>();
let stopWaiting: (() => void) | null = null;

/** Swap in a new snapshot and notify; no-op when nothing changed. */
function patch(next: Partial<AppUpdateSnapshot>): void {
  const merged = { ...snapshot, ...next };
  if (
    merged.updateAvailable === snapshot.updateAvailable &&
    merged.reloadPending === snapshot.reloadPending
  ) {
    return;
  }
  snapshot = merged;
  for (const listener of listeners) listener();
}

/** Record whether the deployed build differs from this bundle. */
export function setUpdateAvailable(updateAvailable: boolean): void {
  patch({ updateAvailable });
}

/**
 * The chip's click: reload now, or — while an edit is unsent, mid-save, or
 * failed with its retry — as soon as that clears. The status line beside the
 * chip already says what is holding it up. A reload here would drop the last
 * few seconds of typing: nothing flushes the editors' debounce on unload.
 */
export function requestReload(): void {
  if (!hasUnsavedWork()) {
    reloadPage();
    return;
  }
  if (stopWaiting) return;
  patch({ reloadPending: true });
  stopWaiting = subscribeToUnsavedWork(() => {
    if (hasUnsavedWork()) return;
    stopWaiting?.();
    stopWaiting = null;
    reloadPage();
  });
}

/** Subscribe to every change; returns the unsubscribe. */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The current snapshot; referentially stable until the next change. */
export function getSnapshot(): AppUpdateSnapshot {
  return snapshot;
}

/** Tests only: back to "no update", nothing waiting, no listeners. */
export function resetAppUpdateStore(): void {
  stopWaiting?.();
  stopWaiting = null;
  snapshot = { updateAvailable: false, reloadPending: false };
  listeners.clear();
}
